import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Home, Compass, Plus, Briefcase, Coins } from 'lucide-react';
import { cn } from '@/lib/utils';

import { useTurnkeyWallet } from '@/providers/TurnkeyProvider';
import { useTurnkeySignIn } from '@/hooks/useTurnkeySignIn';

interface NavItem {
  icon: typeof Home;
  label: string;
  path: string;
  isCreate?: boolean;
}

const navItems: NavItem[] = [
  { icon: Home, label: 'Home', path: '/' },
  { icon: Compass, label: 'Explore', path: '/tokens' },
  { icon: Plus, label: 'Create', path: '/create', isCreate: true },
  { icon: Briefcase, label: 'Portfolio', path: '/portfolio' },
  { icon: Coins, label: 'Rewards', path: '/rewards' },
];

export function BottomNav() {
  const location = useLocation();
  const navigate = useNavigate();

  const { walletAddress, connected } = useTurnkeyWallet();
  const { handleSignIn } = useTurnkeySignIn();

  const handleNavClick = (item: NavItem, e: React.MouseEvent) => {
    // Handle profile navigation
    if (item.path === '/profile') {
      e.preventDefault();
      if (connected && walletAddress) {
        navigate('/creator/' + walletAddress);
      } else {
        handleSignIn();
      }
      return;
    }

    // Handle rewards navigation
    if (item.path === '/rewards') {
      e.preventDefault();
      if (connected && walletAddress) {
        navigate(`/creator/${walletAddress}/rewards`);
      } else {
        handleSignIn();
      }
      return;
    }
  };

  const isPathActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    if (path === '/profile') {
      return location.pathname.startsWith('/creator/') && !location.pathname.endsWith('/rewards');
    }
    if (path === '/rewards') {
      return location.pathname.endsWith('/rewards');
    }
    return location.pathname.startsWith(path);
  };

  return (
    <nav
      className={cn(
        'fixed bottom-0 left-0 right-0 z-50',
        'bg-black/95 backdrop-blur-lg',
        'border-t border-white/10',
        'md:hidden' // Hide on desktop
      )}
      style={{
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      }}
      data-testid="bottom-nav"
    >
      <div className="relative flex items-center justify-around h-14 px-2 overflow-visible">
        {navItems.map((item) => {
          const isActive = isPathActive(item.path);
          const Icon = item.icon;

          // Create button has special styling (no pill indicator for this)
          if (item.isCreate) {
            return (
              <Link
                key={item.path}
                to={item.path}
                className={cn(
                  'flex items-center justify-center',
                  'w-14 h-14 -mt-4 rounded-full',
                  'bg-primary text-primary-foreground',
                  'shadow-lg shadow-primary/30',
                  'transition-transform duration-200',
                  'active:scale-95'
                )}
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                <Icon className="w-6 h-6" strokeWidth={2.5} />
              </Link>
            );
          }

          return (
            <Link
              key={item.path}
              to={item.path === '/profile' || item.path === '/rewards' ? '#' : item.path}
              onClick={(e) => handleNavClick(item, e)}
              className={cn(
                'relative flex flex-col items-center justify-center',
                'min-w-[56px] min-h-[44px] py-1',
                'transition-all duration-150',
                'active:scale-95 active:opacity-80',
                isActive
                  ? 'text-primary'
                  : 'text-muted-foreground'
              )}
              data-testid={`nav-${item.label.toLowerCase()}`}
            >
              <Icon
                className={cn(
                  'w-6 h-6 mb-0.5',
                  isActive && 'stroke-[2.5px]'
                )}
              />
              <span className={cn(
                'text-[10px] font-medium',
                isActive && 'font-semibold'
              )}>
                {item.label}
              </span>
            </Link>
          );
        })}

      </div>
    </nav>
  );
}
